import type { App } from 'vue'
import { KyHttpClient } from '#/infrastructure/persistence/KyHttpClient'
import { HttpRoleRepository } from '#/infrastructure/implementation/role/HttpRoleRepository'
import { HttpFeatureFlagRepository } from '#/infrastructure/implementation/featureFlag/HttpFeatureFlagRepository'
import { HttpCustomerAccessRepository } from '#/infrastructure/implementation/customerAccess/HttpCustomerAccessRepository'
import { HttpConnectionRepository } from '#/infrastructure/implementation/connection/HttpConnectionRepository'
import { HttpOrganizationRepository } from '#/infrastructure/implementation/organization/HttpOrganizationRepository'
import { HttpApplicationRepository } from '#/infrastructure/implementation/application/HttpApplicationRepository'
import { HttpInvitationRepository } from '#/infrastructure/implementation/invitation/HttpInvitationRepository'
import { HttpAssetsLinkRepository } from '#/infrastructure/implementation/assetsLink/HttpAssetsLinkRepository'
import { AzureBlobAssetUploadGateway } from '#/infrastructure/implementation/assetsLink/AzureBlobAssetUploadGateway'
import { HttpUserRepository } from '#/infrastructure/implementation/user/HttpUserRepository'
import { HttpCatalogRepository } from '#/infrastructure/implementation/catalog/HttpCatalogRepository'
import { HttpAuditLogRepository } from '#/infrastructure/implementation/audit/HttpAuditLogRepository'
import { reportInvalidApplication } from './reportInvalidApplication'

import { GetAuditLogUseCase } from '#/application/audit/GetAuditLogUseCase'
import { GetRoleListUseCase } from '#/application/role/GetRoleListUseCase'
import { GetAllProductsFeatureFlagsUseCase } from '#/application/featureFlag/GetAllProductsFeatureFlagsUseCase'
import { ToggleFeatureFlagUseCase } from '#/application/featureFlag/ToggleFeatureFlagUseCase'
import { IsFeatureEnabledUseCase } from '#/application/featureFlag/IsFeatureEnabledUseCase'
import { GetAccessibleCustomersUseCase } from '#/application/customerAccess/GetAccessibleCustomersUseCase'
import { GetCustomerFeaturesUseCase } from '#/application/customerAccess/GetCustomerFeaturesUseCase'
import { GetAllFeaturesUseCase } from '#/application/customerAccess/GetAllFeaturesUseCase'
import { GrantCustomerFeatureUseCase } from '#/application/customerAccess/GrantCustomerFeatureUseCase'
import { RevokeCustomerFeatureUseCase } from '#/application/customerAccess/RevokeCustomerFeatureUseCase'
import { GetConnectionListUseCase } from '#/application/connection/GetConnectionListUseCase'
import { CreateConnectionUseCase } from '#/application/connection/CreateConnectionUseCase'
import { GetOrganizationListUseCase } from '#/application/organization/GetOrganizationListUseCase'
import { GetOrganizationUseCase } from '#/application/organization/GetOrganizationUseCase'
import { FindAllOrganizationsUseCase } from '#/application/organization/FindAllOrganizationsUseCase'
import { CreateOrganizationUseCase } from '#/application/organization/CreateOrganizationUseCase'
import { UpdateOrganizationUseCase } from '#/application/organization/UpdateOrganizationUseCase'
import { DeleteOrganizationUseCase } from '#/application/organization/DeleteOrganizationUseCase'
import { GetApplicationListUseCase } from '#/application/application/GetApplicationListUseCase'
import { GetInvitationListUseCase } from '#/application/invitation/GetInvitationListUseCase'
import { CreateInvitationsUseCase } from '#/application/invitation/CreateInvitationsUseCase'
import { ResendInvitationUseCase } from '#/application/invitation/ResendInvitationUseCase'
import { GetAssetsLinkUseCase } from '#/application/assetsLink/GetAssetsLinkUseCase'
import { CreateAssetsLinkUseCase } from '#/application/assetsLink/CreateAssetsLinkUseCase'
import { UploadAssetUseCase } from '#/application/assetsLink/UploadAssetUseCase'
import { GetUserUseCase } from '#/application/user/GetUserUseCase'
import { GetUserListUseCase } from '#/application/user/GetUserListUseCase'
import { GetUserLogsUseCase } from '#/application/user/GetUserLogsUseCase'
import { GetUserApplicationsUseCase } from '#/application/user/GetUserApplicationsUseCase'
import { GetUserRolesUseCase } from '#/application/user/GetUserRolesUseCase'
import { AssignRolesUseCase } from '#/application/user/AssignRolesUseCase'
import { RemoveRolesUseCase } from '#/application/user/RemoveRolesUseCase'
import { RemoveMemberUseCase } from '#/application/user/RemoveMemberUseCase'
import { UpdateUserMeUseCase } from '#/application/user/UpdateUserMeUseCase'
import { GetCatalogMetricListUseCase } from '#/application/catalog/GetCatalogMetricListUseCase'
import { GetCatalogLayerListUseCase } from '#/application/catalog/GetCatalogLayerListUseCase'
import { GetCatalogApplicationListUseCase } from '#/application/catalog/GetCatalogApplicationListUseCase'
import { GetCatalogMetricCategoryListUseCase } from '#/application/catalog/GetCatalogMetricCategoryListUseCase'
import { GetAssignedMetricIdsUseCase } from '#/application/catalog/GetAssignedMetricIdsUseCase'
import { BulkAssignMetricsToOrganizationUseCase } from '#/application/catalog/BulkAssignMetricsToOrganizationUseCase'
import { ApplyFireriskMetricPresetUseCase } from '#/application/catalog/ApplyFireriskMetricPresetUseCase'
import { UnassignMetricFromOrganizationUseCase } from '#/application/catalog/UnassignMetricFromOrganizationUseCase'
import { GetMetricByOrgIdUseCase } from '#/application/catalog/GetMetricByOrgIdUseCase'
import { GetLayerByOrgIdUseCase } from '#/application/catalog/GetLayerByOrgIdUseCase'
import { GetLayerCategoriesByOrgIdUseCase } from '#/application/catalog/GetLayerCategoriesByOrgIdUseCase'
import { AssignLayerUseCase } from '#/application/catalog/AssignLayerUseCase'
import { UnassignLayerUseCase } from '#/application/catalog/UnassignLayerUseCase'

import { InjectionTypes } from '@/constants/InjectionTypes'

export function bootstrap(app: App) {
  const httpClient = new KyHttpClient('/api')

  const roleRepository = new HttpRoleRepository(httpClient)
  const featureFlagRepository = new HttpFeatureFlagRepository(httpClient)
  const customerAccessRepository = new HttpCustomerAccessRepository(httpClient)
  const connectionRepository = new HttpConnectionRepository(httpClient)
  const organizationRepository = new HttpOrganizationRepository(httpClient)
  const applicationRepository = new HttpApplicationRepository(httpClient, reportInvalidApplication)
  const invitationRepository = new HttpInvitationRepository(httpClient)
  const assetsLinkRepository = new HttpAssetsLinkRepository(httpClient)
  const assetUploadGateway = new AzureBlobAssetUploadGateway()
  const userRepository = new HttpUserRepository(httpClient)
  const catalogRepository = new HttpCatalogRepository(httpClient)
  const auditLogRepository = new HttpAuditLogRepository(httpClient)

  app.provide(InjectionTypes.RoleRepository, roleRepository)
  app.provide(InjectionTypes.FeatureFlagRepository, featureFlagRepository)
  app.provide(InjectionTypes.CustomerAccessRepository, customerAccessRepository)
  app.provide(InjectionTypes.ConnectionRepository, connectionRepository)
  app.provide(InjectionTypes.OrganizationRepository, organizationRepository)
  app.provide(InjectionTypes.ApplicationRepository, applicationRepository)
  app.provide(InjectionTypes.InvitationRepository, invitationRepository)
  app.provide(InjectionTypes.AssetsLinkRepository, assetsLinkRepository)
  app.provide(InjectionTypes.AssetUploadGateway, assetUploadGateway)
  app.provide(InjectionTypes.UserRepository, userRepository)
  app.provide(InjectionTypes.CatalogRepository, catalogRepository)
  app.provide(InjectionTypes.AuditLogRepository, auditLogRepository)

  app.provide(InjectionTypes.GetRoleListUseCase, new GetRoleListUseCase(roleRepository))
  app.provide(InjectionTypes.GetAuditLogUseCase, new GetAuditLogUseCase(auditLogRepository))

  app.provide(
    InjectionTypes.GetAllProductsFeatureFlagsUseCase,
    new GetAllProductsFeatureFlagsUseCase(featureFlagRepository),
  )
  app.provide(
    InjectionTypes.ToggleFeatureFlagUseCase,
    new ToggleFeatureFlagUseCase(featureFlagRepository),
  )
  app.provide(
    InjectionTypes.IsFeatureEnabledUseCase,
    new IsFeatureEnabledUseCase(featureFlagRepository),
  )

  app.provide(
    InjectionTypes.GetAccessibleCustomersUseCase,
    new GetAccessibleCustomersUseCase(customerAccessRepository),
  )
  app.provide(
    InjectionTypes.GetCustomerFeaturesUseCase,
    new GetCustomerFeaturesUseCase(customerAccessRepository),
  )
  app.provide(
    InjectionTypes.GetAllFeaturesUseCase,
    new GetAllFeaturesUseCase(customerAccessRepository),
  )
  app.provide(
    InjectionTypes.GrantCustomerFeatureUseCase,
    new GrantCustomerFeatureUseCase(customerAccessRepository),
  )
  app.provide(
    InjectionTypes.RevokeCustomerFeatureUseCase,
    new RevokeCustomerFeatureUseCase(customerAccessRepository),
  )

  app.provide(
    InjectionTypes.GetConnectionListUseCase,
    new GetConnectionListUseCase(connectionRepository),
  )
  app.provide(
    InjectionTypes.CreateConnectionUseCase,
    new CreateConnectionUseCase(connectionRepository),
  )

  app.provide(
    InjectionTypes.GetOrganizationListUseCase,
    new GetOrganizationListUseCase(organizationRepository),
  )
  app.provide(
    InjectionTypes.GetOrganizationUseCase,
    new GetOrganizationUseCase(organizationRepository),
  )
  app.provide(
    InjectionTypes.FindAllOrganizationsUseCase,
    new FindAllOrganizationsUseCase(organizationRepository),
  )
  app.provide(
    InjectionTypes.CreateOrganizationUseCase,
    new CreateOrganizationUseCase(organizationRepository),
  )
  app.provide(
    InjectionTypes.UpdateOrganizationUseCase,
    new UpdateOrganizationUseCase(organizationRepository),
  )
  app.provide(
    InjectionTypes.DeleteOrganizationUseCase,
    new DeleteOrganizationUseCase(organizationRepository),
  )

  app.provide(
    InjectionTypes.GetApplicationListUseCase,
    new GetApplicationListUseCase(applicationRepository),
  )

  app.provide(
    InjectionTypes.GetInvitationListUseCase,
    new GetInvitationListUseCase(invitationRepository),
  )
  app.provide(
    InjectionTypes.CreateInvitationsUseCase,
    new CreateInvitationsUseCase(invitationRepository),
  )
  app.provide(
    InjectionTypes.ResendInvitationUseCase,
    new ResendInvitationUseCase(invitationRepository),
  )

  app.provide(InjectionTypes.GetAssetsLinkUseCase, new GetAssetsLinkUseCase(assetsLinkRepository))
  app.provide(
    InjectionTypes.CreateAssetsLinkUseCase,
    new CreateAssetsLinkUseCase(assetsLinkRepository),
  )
  app.provide(InjectionTypes.UploadAssetUseCase, new UploadAssetUseCase(assetUploadGateway))

  app.provide(InjectionTypes.GetUserUseCase, new GetUserUseCase(userRepository))
  app.provide(InjectionTypes.GetUserListUseCase, new GetUserListUseCase(userRepository))
  app.provide(InjectionTypes.GetUserLogsUseCase, new GetUserLogsUseCase(userRepository))
  app.provide(
    InjectionTypes.GetUserApplicationsUseCase,
    new GetUserApplicationsUseCase(userRepository),
  )
  app.provide(InjectionTypes.GetUserRolesUseCase, new GetUserRolesUseCase(userRepository))
  app.provide(InjectionTypes.AssignRolesUseCase, new AssignRolesUseCase(userRepository))
  app.provide(InjectionTypes.RemoveRolesUseCase, new RemoveRolesUseCase(userRepository))
  app.provide(InjectionTypes.RemoveMemberUseCase, new RemoveMemberUseCase(userRepository))
  app.provide(InjectionTypes.UpdateUserMeUseCase, new UpdateUserMeUseCase(userRepository))

  app.provide(
    InjectionTypes.GetCatalogMetricListUseCase,
    new GetCatalogMetricListUseCase(catalogRepository),
  )
  app.provide(
    InjectionTypes.GetCatalogLayerListUseCase,
    new GetCatalogLayerListUseCase(catalogRepository),
  )
  app.provide(
    InjectionTypes.GetCatalogApplicationListUseCase,
    new GetCatalogApplicationListUseCase(catalogRepository),
  )

  app.provide(
    InjectionTypes.GetCatalogMetricCategoryListUseCase,
    new GetCatalogMetricCategoryListUseCase(catalogRepository),
  )
  app.provide(
    InjectionTypes.GetAssignedMetricIdsUseCase,
    new GetAssignedMetricIdsUseCase(catalogRepository),
  )
  app.provide(
    InjectionTypes.BulkAssignMetricsToOrganizationUseCase,
    new BulkAssignMetricsToOrganizationUseCase(catalogRepository),
  )
  app.provide(
    InjectionTypes.UnassignMetricFromOrganizationUseCase,
    new UnassignMetricFromOrganizationUseCase(catalogRepository),
  )
  app.provide(
    InjectionTypes.ApplyFireriskMetricPresetUseCase,
    new ApplyFireriskMetricPresetUseCase(catalogRepository),
  )

  app.provide(
    InjectionTypes.GetMetricByOrgIdUseCase,
    new GetMetricByOrgIdUseCase(catalogRepository),
  )
  app.provide(InjectionTypes.GetLayerByOrgIdUseCase, new GetLayerByOrgIdUseCase(catalogRepository))
  app.provide(
    InjectionTypes.GetLayerCategoriesByOrgIdUseCase,
    new GetLayerCategoriesByOrgIdUseCase(catalogRepository),
  )
  app.provide(InjectionTypes.AssignLayerUseCase, new AssignLayerUseCase(catalogRepository))
  app.provide(InjectionTypes.UnassignLayerUseCase, new UnassignLayerUseCase(catalogRepository))

  return { userRepository }
}
